import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import { PaginatedResponse, PaginationRequest } from "@/shared/types";
import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import { WorkerCommissionExclusion, WorkerV2 } from "../model";
import {
  AddCommissionExclusionRequest,
  DeactivateCommissionExclusionRequest,
  GetComissionsExclusionsRequest,
  GetMarchGiveawayStatsRequest,
  GetMarchGiveawayTicketsRequest,
  MarchGiveawayStatsResponse,
  MarchGiveawayTicketsResponse,
} from "./api.dto";
import { useToastNotifications } from "@/shared/hooks";

export const useWorkers = (
  props: PaginationRequest & { isFree: boolean | null },
) => {
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<WorkerV2>>({
    queryKey: ["workers", params],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<WorkerV2>>(
        "/workers",
        params,
      );
      return response.data;
    },
  });
};

export const useWorkersInfiniteScroll = (
  params: Omit<PaginationRequest & { isFree?: boolean | null }, "currentPage">,
) => {
  return useInfiniteQuery<PaginatedResponse<WorkerV2>>({
    queryKey: ["workersInfinite", filterObjectKeys(params)],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<WorkerV2>>(
        "/workers",
        {
          ...filterObjectKeys(params),
          currentPage: pageParam,
        },
      );

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = params.pageSize;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });
};

export const useCommissionExclusions = (
  props: GetComissionsExclusionsRequest & { enabled?: boolean },
) => {
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<WorkerCommissionExclusion>>({
    queryKey: ["workers", "commissionExclusions", params],
    queryFn: async () => {
      const response = await apiInstance.get<
        PaginatedResponse<WorkerCommissionExclusion>
      >("/workers/commission-exclusions", params, true);
      return response.data;
    },
    enabled: props.enabled,
  });
};

export const useAddCommissionExclusion = ({
  workerId,
}: {
  workerId: string;
}) => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Исключение добавлено.",
      },
    },
    refetchQueries: ["workers", "commissionExclusions"],
  });
  return useMutation({
    mutationKey: ["addCommissionExclusion"],
    mutationFn: async (data: AddCommissionExclusionRequest) => {
      const response = await apiInstance.post(
        `/workers/${workerId}/commission-exclusions`,
        data,
        undefined,
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useDeactivateCommissionExclusion = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Исключение отключено.",
      },
    },
    refetchQueries: ["workers", "commissionExclusions"],
  });
  return useMutation({
    mutationKey: ["deactivateCommissionExclusion"],
    mutationFn: async ({ id }: DeactivateCommissionExclusionRequest) => {
      const response = await apiInstance.post(
        `/workers/commission-exclusions/${id}/deactivate`,
        undefined,
        undefined,
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useMarchGiveawayTickets = (
  params: GetMarchGiveawayTicketsRequest,
) => {
  return useQuery<MarchGiveawayTicketsResponse>({
    queryKey: ["marchGiveawayTickets", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await apiInstance.get<MarchGiveawayTicketsResponse>(
        "/workers/giveaways/spring/march/tickets",
        filterObjectKeys(params),
        true,
      );
      return { ...response.data, serverDate: response.headers["date"] ?? "" };
    },
  });
};

export const useMarchGiveawayStats = (params: GetMarchGiveawayStatsRequest) => {
  return useQuery<MarchGiveawayStatsResponse>({
    queryKey: ["marchGiveawayStats", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await apiInstance.get<MarchGiveawayStatsResponse>(
        "/workers/giveaways/spring/march/stats",
        filterObjectKeys(params),
        true,
      );
      return { ...response.data, serverDate: response.headers["date"] ?? "" };
    },
  });
};
